import { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Button, Card, Grid, Group, Select, Text } from '@mantine/core';
import useFetch from '../../../helpers/useFetch';
import ErrorMessage from '../../ErrorMessage';
import FileUpload from '../../FileUpload';

const SettingsTabAppPush = forwardRef(({ domain, onChange }, ref) => {
  // commenting out logo related elements, as we will not be generating them currently
  // not removing them because we will use them in another tool
  const [loadError, setLoadError] = useState();
  const [userError, setUserError] = useState();
  const [firebaseAccounts, setFirebaseAccounts] = useState([]);
  const [originalAppType, setOriginalAppType] = useState(domain.appType || 'unified');
  const [appType, setAppType] = useState(domain.appType || 'unified');
  const [originalApnsSandbox, setOriginalApnsSandbox] = useState(domain.apnsSandbox === true);
  const [apnsSandbox, setApnsSandbox] = useState(domain.apnsSandbox === true);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [deletedPlatforms, setDeletedPlatforms] = useState([]);
  const fetch = useFetch();

  useEffect(() => {
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/firebaseAccounts`)
      .then(({ firebaseAccounts }) => setFirebaseAccounts(firebaseAccounts || []))
      .catch(setLoadError);
  }, [domain.accountId, domain.id, fetch]);

  // Update state when domain prop changes (after save)
  useEffect(() => {
    setAppType(domain.appType || 'unified');
    setOriginalAppType(domain.appType || 'unified');
  }, [domain.appType]);

  useEffect(() => {
    setApnsSandbox(domain.apnsSandbox === true);
    setOriginalApnsSandbox(domain.apnsSandbox === true);
  }, [domain.apnsSandbox]);

  // Notify parent when there are unsaved changes
  useEffect(() => {
    const hasChanges =
      Object.keys(selectedFiles).length > 0 ||
      deletedPlatforms.length > 0 ||
      appType !== originalAppType ||
      apnsSandbox !== originalApnsSandbox;
    if (onChange) {
      onChange(hasChanges);
    }
  }, [selectedFiles, deletedPlatforms, appType, originalAppType, apnsSandbox, originalApnsSandbox, onChange]);

  const handleAppTypeChange = (newAppType) => {
    setAppType(newAppType);
  };

  const handleFileSelect = (files, platform) => {
    if (files && files.length > 0) {
      setSelectedFiles((prev) => ({
        ...prev,
        [platform]: files[0],
      }));
    }
  };

  const handleDeleteCredentials = (platform) => {
    if (window.confirm(`Are you sure you want to delete these ${platform === 'both' ? '' : platform + ' '}credentials?`)) {
      // If there's a selected file, just remove it from selectedFiles
      if (selectedFiles[platform]) {
        setSelectedFiles((prev) => {
          const updated = { ...prev };
          delete updated[platform];
          return updated;
        });
      }

      // If there are saved credentials, stage the deletion
      if (getCredentialsForPlatform(platform)) {
        setDeletedPlatforms((prev) => [...prev, platform]);
        // Remove from UI immediately for better UX
        setFirebaseAccounts((prev) => prev.filter((acc) => acc.platform !== platform));
      }
    }
  };

  const saveChanges = async () => {
    setUserError(null);

    try {
      // Save the domain settings this tab owns, but only the ones that changed
      const changedDomainSettings = {
        ...(appType !== originalAppType && { appType }),
        ...(apnsSandbox !== originalApnsSandbox && { apnsSandbox }),
      };
      if (Object.keys(changedDomainSettings).length > 0) {
        await fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}`, {
          method: 'PUT',
          body: JSON.stringify(changedDomainSettings),
        });
      }

      // Process deletions
      for (const platform of deletedPlatforms) {
        await fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/firebaseAccounts/${platform}`, {
          method: 'DELETE',
        });
      }

      // Process file uploads
      for (const [platform, file] of Object.entries(selectedFiles)) {
        const reader = new FileReader();

        await new Promise((resolve, reject) => {
          reader.onload = async function (e) {
            try {
              const parsed = JSON.parse(e.target.result);

              await fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/firebaseAccounts`, {
                method: 'POST',
                body: JSON.stringify({
                  projectId: parsed.project_id,
                  privateKeyId: parsed.private_key_id,
                  privateKey: parsed.private_key,
                  clientEmail: parsed.client_email,
                  clientId: parsed.client_id,
                  authUri: parsed.auth_uri,
                  tokenUri: parsed.token_uri,
                  authProviderX509CertUrl: parsed.auth_provider_x509_cert_url,
                  clientX509CertUrl: parsed.client_x509_cert_url,
                  universeDomain: parsed.universe_domain,
                  platform: platform,
                }),
              })
                .then(({ firebaseAccount }) => {
                  // Add or update the firebase account in the list
                  setFirebaseAccounts((prev) => {
                    const filtered = prev.filter((acc) => acc.platform !== platform);
                    return [...filtered, firebaseAccount];
                  });
                  resolve();
                })
                .catch(reject);
            } catch (error) {
              reject(new Error('Invalid JSON file'));
            }
          };

          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsText(file);
        });
      }

      // Clear staged changes after successful save
      setSelectedFiles({});
      setDeletedPlatforms([]);
    } catch (error) {
      setUserError(error);
      throw error;
    }
  };

  // Expose saveChanges function to parent component
  useImperativeHandle(ref, () => ({
    saveChanges,
  }));

  // Get credentials for specific platform
  const getCredentialsForPlatform = (platform) => {
    return firebaseAccounts.find((acc) => acc.platform === platform);
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  const appTypeOptions = [
    { value: 'unified', label: 'Unified (Single firebase credentials for iOS and Android)' },
    { value: 'separate', label: 'Separate (Individual firebase credentials for iOS and Android)' },
  ];

  const apnsEnvironmentOptions = [
    { value: 'false', label: 'Production' },
    { value: 'true', label: 'Sandbox' },
  ];

  const renderCredentialsSection = (platform, label) => {
    const credentials = getCredentialsForPlatform(platform);
    const selectedFile = selectedFiles[platform];
    const hasCredentials = credentials || selectedFile;

    return (
      <Grid mb="md">
        <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'flex-start', paddingTop: 8 }}>
          <label>{label}</label>
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 8 }}>
          {hasCredentials && (
            <Group justify="space-between" mb="xs">
              <Text size="sm">{selectedFile?.name || credentials?.clientEmail}</Text>
              <Button
                size="xs"
                color="red"
                onClick={() => handleDeleteCredentials(platform)}
              >
                Delete
              </Button>
            </Group>
          )}
          <FileUpload onFileSet={(files) => handleFileSelect(files, platform)} />
          <Text size="xs" c="dimmed" mt="xs">
            Please provide the Firebase service account credentials .json file.
            <br />
            Your developers can generate this from your Firebase console by going into{' '}
            <strong>Project settings</strong> -&gt; <strong>Service accounts</strong>, and clicking on{' '}
            <strong>Generate new private key</strong>
          </Text>
        </Grid.Col>
      </Grid>
    );
  };

  return (
    <Card withBorder>
      <Card.Section p="md">
        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label>App Type</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Select
              data={appTypeOptions}
              value={appType}
              onChange={(value) => handleAppTypeChange(value)}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Select Unified if your app uses a cross-platform framework (Flutter, React Native, Ionic, etc.) with one
              Firebase project for both platforms. Select Separate if you have distinct native apps for iOS and Android
              with separate Firebase projects.
            </Text>
          </Grid.Col>
        </Grid>

        <Grid mb="md">
          <Grid.Col span={{ base: 12, sm: 4 }} style={{ display: 'flex', alignItems: 'center' }}>
            <label htmlFor="apnsSandbox">iOS APNs environment</label>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Select
              id="apnsSandbox"
              data={apnsEnvironmentOptions}
              allowDeselect={false}
              value={apnsSandbox ? 'true' : 'false'}
              onChange={(value) => setApnsSandbox(value === 'true')}
            />
            <Text size="xs" c="dimmed" mt="xs">
              Sandbox for development builds; Production for TestFlight and App Store builds. Only used when iOS devices
              register APNs tokens (expo-notifications integrations).
            </Text>
          </Grid.Col>
        </Grid>

        {appType === 'unified' && renderCredentialsSection('both', 'Firebase Service Account Credentials')}

        {appType === 'separate' && (
          <>
            {renderCredentialsSection('ios', 'iOS Firebase Credentials')}
            {renderCredentialsSection('android', 'Android Firebase Credentials')}
          </>
        )}
      </Card.Section>
      <ErrorMessage message={userError?.message} />
    </Card>
  );
});

export default SettingsTabAppPush;
